import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, TicketPercent, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useMoney } from '@/lib/hooks/useMoney'
import type { AppliedCoupon } from '@/types/checkout'
import type { CouponError } from '@/lib/hooks/usePurchaseFlow'

interface CouponFieldProps {
  applied: AppliedCoupon | null
  discountAmount: number
  currency: string
  pending: boolean
  error: CouponError | null
  onApply: (code: string) => void
  onRemove: () => void
  /** A subscription bills again: say how long the discount lasts. A one-off purchase has one charge. */
  recurring: boolean
  /** The subscription's interval; an annual plan renews after 12 months, past a short REPEATING coupon. */
  billingInterval?: string | null
  disabled?: boolean
  className?: string
}

/**
 * "Tenho um cupom" (FIN-7): a discreet link that opens a field, and once the
 * server applies the code, one line with the discount and a way to remove it.
 *
 * The field only collects the code. Whether it applies, and how much it takes
 * off, is the server's answer to `calculate-price`; nothing is computed here.
 */
export function CouponField({
  applied,
  discountAmount,
  currency,
  pending,
  error,
  onApply,
  onRemove,
  recurring,
  billingInterval,
  disabled = false,
  className
}: CouponFieldProps) {
  const { t } = useTranslation('checkout')
  const formatMoney = useMoney()
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')

  const errorText = error ? error.message || t(error.key || 'purchase.coupon.invalid') : null

  if (applied) {
    /* The server keeps a REPEATING discount on renewals whose period starts
       within N months of the first. An annual plan's next period starts at
       month 12, so "3 months" there means the first charge only. */
    const months = applied.durationInMonths || 0
    const onceInPractice = applied.duration === 'REPEATING' && billingInterval === 'YEARLY' && months > 0 && months <= 12
    const duration =
      !recurring
        ? null
        : applied.duration === 'ONCE' || onceInPractice
          ? t('purchase.coupon.durationOnce')
          : applied.duration === 'REPEATING' && applied.durationInMonths
            ? t('purchase.coupon.durationRepeating', { count: applied.durationInMonths })
            : applied.duration === 'FOREVER'
              ? t('purchase.coupon.durationForever')
              : null
    return (
      <div className={cn('rounded-lg bg-green-50 px-3 py-2.5', className)}>
        <div className="flex items-center gap-2 text-sm">
          <TicketPercent className="h-4 w-4 flex-shrink-0 text-green-700" />
          <span className="min-w-0 flex-1 text-green-800">
            {t('purchase.coupon.applied', { code: applied.code, amount: formatMoney(discountAmount, currency) })}
          </span>
          <button
            type="button"
            onClick={() => {
              setCode('')
              onRemove()
            }}
            disabled={disabled}
            className="flex flex-shrink-0 items-center gap-1 text-xs font-medium text-green-800 underline hover:no-underline disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
            {t('purchase.coupon.remove')}
          </button>
        </div>
        {duration && <p className="mt-1 pl-6 text-xs text-green-700">{duration}</p>}
      </div>
    )
  }

  if (!open) {
    return (
      <div className={className}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={disabled}
          className="text-sm font-medium text-gray-600 underline underline-offset-2 hover:text-gray-900 disabled:opacity-50"
        >
          {t('purchase.coupon.toggle')}
        </button>
      </div>
    )
  }

  const apply = () => {
    if (!pending && !disabled) onApply(code)
  }

  return (
    <div className={className}>
      <label htmlFor="checkout-coupon" className="mb-1.5 block text-xs text-gray-500">
        {t('purchase.coupon.label')}
      </label>
      <div className="flex gap-2">
        <Input
          id="checkout-coupon"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          /* Enter applies the code; it must never submit the payment form. */
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              apply()
            }
          }}
          placeholder={t('purchase.coupon.placeholder')}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          disabled={disabled || pending}
          aria-invalid={Boolean(errorText)}
          aria-describedby={errorText ? 'checkout-coupon-error' : undefined}
          className="min-w-0 flex-1 uppercase"
        />
        <Button type="button" variant="outline" onClick={apply} disabled={disabled || pending || !code.trim()} className="flex-shrink-0">
          {pending ? <Loader2 className="h-4 w-4 animate-spin" aria-label={t('purchase.coupon.applying')} /> : t('purchase.coupon.apply')}
        </Button>
      </div>
      {errorText && (
        <p id="checkout-coupon-error" role="alert" className="mt-1.5 text-xs text-red-600">
          {errorText}
        </p>
      )}
    </div>
  )
}
